import React, { useEffect, useState } from "react";
import axios from "axios";
import API_URL from "../config";

const API = `${API_URL}/api/configuraciones-horarios`;

const formInicial = {
  nombre: "",
  intervalo_citas_minutos: 15,
  tiempo_cancelacion_minutos: 0,
  dias_ocultar_antes: 0,
  horas_ocultar_antes: 0,
  minutos_ocultar_antes: 0,
  dias_visibles_futuro: 0,
};

const GestionarConfiguracionesHorarios = () => {
  const [configuraciones, setConfiguraciones] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [form, setForm] = useState(formInicial);
  const [editandoId, setEditandoId] = useState(null);

  const cargar = async () => {
    const res = await axios.get(`${API}?t=${Date.now()}`);
    setConfiguraciones(res.data || []);
  };

  const cargarServicios = async () => {
    const res = await axios.get(`${API_URL}/api/servicios?t=${Date.now()}`);
    setServicios(res.data || []);
  };

  useEffect(() => {
    cargar();
    cargarServicios();
  }, []);

  const asignarConfiguracion = async (servicioId, configuracionId) => {
    await axios.post(`${API_URL}/api/servicios/${servicioId}/configuracion`, {
      configuracion_id: configuracionId || null,
    });
    cargarServicios();
  };

  const limpiar = () => {
    setForm(formInicial);
    setEditandoId(null);
  };

  const guardar = async () => {
    if (!form.nombre.trim()) {
      alert("⚠️ Ponle un nombre a la configuración");
      return;
    }

    if (editandoId) {
      await axios.post(`${API}/${editandoId}`, form);
    } else {
      await axios.post(API, form);
    }

    limpiar();
    cargar();
  };

  const editar = (c) => {
    setEditandoId(c.id);
    setForm({
      nombre: c.nombre,
      intervalo_citas_minutos: c.intervalo_citas_minutos,
      tiempo_cancelacion_minutos: c.tiempo_cancelacion_minutos,
      dias_ocultar_antes: c.dias_ocultar_antes,
      horas_ocultar_antes: c.horas_ocultar_antes,
      minutos_ocultar_antes: c.minutos_ocultar_antes,
      dias_visibles_futuro: c.dias_visibles_futuro,
    });
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar esta configuración? Los servicios que la tengan asignada volverán al sistema anterior.")) return;
    await axios.post(`${API}/${id}/eliminar`);
    cargar();
  };

  const campo = (label, key, hint) => (
    <div className="campo">
      <label>{label}</label>
      <input
        type="number"
        min="0"
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="border p-2 rounded w-full"
      />
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );

  return (
    <div>
      <h2 className="text-2xl font-bold mb-2">⚙️ Configuraciones de Horarios</h2>
      <p className="text-gray-600 mb-6">
        Crea perfiles de configuración y asígnalos a cada servicio desde
        "Gestionar Servicios". Un servicio sin configuración asignada sigue
        usando el sistema anterior.
      </p>

      {/* FORMULARIO */}
      <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 border">
        <h3 className="text-lg font-semibold mb-4">
          {editandoId ? "✏️ Editar configuración" : "➕ Nueva configuración"}
        </h3>

        <div className="campo mb-4">
          <label>Nombre de la configuración</label>
          <input
            value={form.nombre}
            onChange={(e) => setForm({ ...form, nombre: e.target.value })}
            placeholder="Ej. Colegio estándar, CDA rápido..."
            className="border p-2 rounded w-full"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {campo("Duración de citas (min)", "intervalo_citas_minutos")}
          {campo("Tiempo mínimo de cancelación (min)", "tiempo_cancelacion_minutos")}
          {campo("Días antes de ocultar horarios", "dias_ocultar_antes")}
          {campo("Horas antes de ocultar horarios", "horas_ocultar_antes")}
          {campo("Minutos antes de ocultar horarios", "minutos_ocultar_antes")}
          {campo(
            "Días visibles hacia el futuro",
            "dias_visibles_futuro",
            "0 = sin límite (muestra todos los horarios futuros)"
          )}
        </div>

        <div className="mt-6 flex gap-3">
          <button
            onClick={guardar}
            className="bg-indigo-600 text-white px-5 py-2 rounded-xl hover:bg-indigo-700"
          >
            💾 Guardar
          </button>
          {editandoId && (
            <button
              onClick={limpiar}
              className="bg-slate-500 text-white px-5 py-2 rounded-xl hover:bg-slate-600"
            >
              Cancelar
            </button>
          )}
        </div>
      </div>

      {/* TABLA */}
      <div className="overflow-x-auto bg-white rounded-2xl shadow-xl border">
        <table className="w-full border-collapse">
          <thead className="bg-indigo-600 text-white">
            <tr>
              <th className="border px-4 py-3 text-left">Nombre</th>
              <th className="border px-4 py-3 text-center">Duración</th>
              <th className="border px-4 py-3 text-center">Cancelación mín.</th>
              <th className="border px-4 py-3 text-center">Ocultar antes</th>
              <th className="border px-4 py-3 text-center">Días visibles</th>
              <th className="border px-4 py-3 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {configuraciones.map((c) => (
              <tr key={c.id} className="hover:bg-slate-100">
                <td className="border px-4 py-2 font-semibold">{c.nombre}</td>
                <td className="border px-4 py-2 text-center">{c.intervalo_citas_minutos} min</td>
                <td className="border px-4 py-2 text-center">{c.tiempo_cancelacion_minutos} min</td>
                <td className="border px-4 py-2 text-center">
                  {c.dias_ocultar_antes}d {c.horas_ocultar_antes}h {c.minutos_ocultar_antes}m
                </td>
                <td className="border px-4 py-2 text-center">
                  {Number(c.dias_visibles_futuro) === 0 ? "Sin límite" : `${c.dias_visibles_futuro} días`}
                </td>
                <td className="border px-4 py-2 text-center space-x-2">
                  <button onClick={() => editar(c)}>✏️</button>
                  <button onClick={() => eliminar(c.id)}>🗑</button>
                </td>
              </tr>
            ))}
            {configuraciones.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center p-6 text-gray-500">
                  No hay configuraciones creadas todavía.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ASIGNAR A SERVICIOS */}
      <h3 className="text-xl font-bold mt-10 mb-3">
        🔗 Asignar configuración a cada servicio
      </h3>
      <p className="text-gray-600 mb-4">
        Un servicio sin configuración asignada sigue usando el sistema
        anterior, sin ningún cambio.
      </p>

      <div className="overflow-x-auto bg-white rounded-2xl shadow-xl border">
        <table className="w-full border-collapse">
          <thead className="bg-indigo-600 text-white">
            <tr>
              <th className="border px-4 py-3 text-left">Servicio</th>
              <th className="border px-4 py-3 text-left">Configuración asignada</th>
            </tr>
          </thead>
          <tbody>
            {servicios.map((s) => (
              <tr key={s.id} className="hover:bg-slate-100">
                <td className="border px-4 py-2 font-semibold">{s.nombre}</td>
                <td className="border px-4 py-2">
                  <select
                    value={s.configuracion_id || ""}
                    onChange={(e) =>
                      asignarConfiguracion(s.id, e.target.value || null)
                    }
                    className="border p-2 rounded w-full max-w-xs"
                  >
                    <option value="">Sistema anterior</option>
                    {configuraciones.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nombre}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
            {servicios.length === 0 && (
              <tr>
                <td colSpan={2} className="text-center p-6 text-gray-500">
                  No hay servicios creados todavía.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GestionarConfiguracionesHorarios;
